import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type SwiperClass from "swiper/types/swiper-class";
import { useBreakpoints } from "@otrium/core";
import { ArrowBack } from "src/icons/ArrowBack";
import { ArrowNext } from "src/icons/ArrowNext";
import { BrandCardFallback } from "src/molecules/BrandCard";
import ProductCardFallback from "src/molecules/ProductCardSkeleton/ProductCardSkeleton";
import {
  CarouselMarginSize,
  CarouselTemplate,
  StyledButtonBack,
  StyledButtonNext,
  StyledSwiperWrapper,
} from "./Carousel.styled";
import { useDebouncedCallback } from "use-debounce";
import { isEqualArray } from "src/utils/equal-array";
import { useInView } from "react-intersection-observer";
import classNames from "classnames";
import "src/utils/swiper-setup";

type CarouselProps = {
  items: Array<ReactNode>;
  slidesDesktop?: number;
  slidesTablet?: number;
  slidesMobile?: number;
  maxItems?: number;
  marginSize?: CarouselMarginSize;
  marginSizeMobile?: CarouselMarginSize | undefined;
  shouldReset?: boolean;
  isProductCarousel?: boolean;
  imageElementWrapperHeight?: number | null | undefined;
  slideShadowSpace?: number;
  hasVerticalPadding?: boolean;
  isRetroRevival?: boolean;
  trackCarouselViewed?: (visibleIndexes: number[]) => void;
};

function Carousel({
  items,
  slidesDesktop,
  slidesTablet,
  slidesMobile,
  maxItems,
  marginSize = "l",
  marginSizeMobile,
  shouldReset,
  imageElementWrapperHeight,
  isProductCarousel = true,
  slideShadowSpace,
  hasVerticalPadding = true,
  isRetroRevival = false,
  trackCarouselViewed,
}: CarouselProps): JSX.Element {
  const [isClient, setIsClient] = useState(false);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [edges, setEdges] = useState({ isBeginning: true, isEnd: false });
  const [hasHorizontalPadding, setHasHorizontalPadding] = useState(true);
  const [ref] = useInView({
    threshold: 0,
    triggerOnce: true,
    skip: !isClient,
    initialInView: false,
    onChange(inView) {
      if (!inView) {
        return;
      }

      let visibleIndexes: number[] = [];

      if (swiper && "visibleSlides" in swiper) {
        visibleIndexes =
          (swiper?.visibleSlides as HTMLElement[])?.map((item) =>
            Number(item.dataset.itemIndex)
          ) || [];
      }

      if (visibleIndexes.length && trackCarouselViewed) {
        trackCarouselViewed(visibleIndexes);
      }
    },
  });

  const prevButton = useRef<HTMLButtonElement>(null);
  const nextButton = useRef<HTMLButtonElement>(null);
  const spaceBetweenTabletAndDesktop =
    marginSize === "s"
      ? 8
      : marginSize === "m"
      ? 16
      : marginSize === "l"
      ? 24
      : 32; // NOTE: "xl"
  const spaceBetweenMobile =
    marginSizeMobile === "s"
      ? 4
      : marginSizeMobile === "m"
      ? 8
      : marginSizeMobile === "l"
      ? 16
      : 24; // NOTE: "xl"

  // the slides takes a property `visibleSlides`
  // this vary per breakpoint
  const { isTablet, isDesktop } = useBreakpoints();
  slideShadowSpace = useMemo(
    () => (isDesktop ? slideShadowSpace : undefined),
    [isDesktop, slideShadowSpace]
  );

  const spaceBetween = useMemo(
    () =>
      !isDesktop && !isTablet && marginSizeMobile
        ? spaceBetweenMobile
        : spaceBetweenTabletAndDesktop,
    [
      isDesktop,
      isTablet,
      marginSizeMobile,
      spaceBetweenMobile,
      spaceBetweenTabletAndDesktop,
    ]
  );

  const arrowTopPositionCalculated = useCallback(
    (additionalSpace?: number) => {
      if (
        slidesDesktop &&
        imageElementWrapperHeight &&
        items.length > slidesDesktop
      ) {
        return `${imageElementWrapperHeight / 2 + (additionalSpace || 0)}px`;
      }

      return null;
    },
    [slidesDesktop, imageElementWrapperHeight, items.length]
  );

  const arrowTopPosition = useMemo(
    () => arrowTopPositionCalculated(slideShadowSpace),
    [slideShadowSpace, arrowTopPositionCalculated]
  );

  // visible slides per breakpoint
  // Desktop -> 3 slides
  // Tablet -> 2 slides
  // Mobile -> 1 slide
  const visibleSlides = useMemo(
    () =>
      isDesktop
        ? slidesDesktop || 3
        : isTablet
        ? slidesTablet || 2
        : slidesMobile || 1,
    [isDesktop, isTablet, slidesDesktop, slidesTablet, slidesMobile]
  );

  const updateEdges = useCallback((swiperInstance: SwiperClass) => {
    setEdges((current) =>
      current.isBeginning === swiperInstance.isBeginning &&
      current.isEnd === swiperInstance.isEnd
        ? current
        : {
            isBeginning: swiperInstance.isBeginning,
            isEnd: swiperInstance.isEnd,
          }
    );
  }, []);

  const onSwiper = useCallback(
    (swiperInstance: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setSwiper(swiperInstance);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      updateEdges(swiperInstance);
    },
    [setSwiper, updateEdges]
  );

  useEffect(() => setIsClient(true), []);

  useEffect(() => {
    let id: number | null = null;
    if (shouldReset && swiper) {
      swiper.slideTo(0);
      // the workaround to fix the issue
      // after reset slide items slide buttons sometimes don't render properly
      id = window.setTimeout(() => {
        swiper.navigation.update();
        updateEdges(swiper);
      }, 10);
    }

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [shouldReset, swiper, items.length, updateEdges]);

  const carouselItems = useMemo(
    () =>
      maxItems && items.length > maxItems ? items.slice(0, maxItems) : items,
    [maxItems, items]
  );

  // Ref to keep track of the previous tracked indexes
  const visibleIndexesRef = useRef<number[]>([]);

  const handleSwipeChange = useCallback(
    (swiper: SwiperClass) => {
      let visibleIndexes: number[] = [];

      if ("visibleSlides" in swiper) {
        visibleIndexes =
          (swiper?.visibleSlides as HTMLElement[])?.map((item) =>
            Number(item.dataset.itemIndex)
          ) || [];
      }

      // Only trigger tracking if there are visible items,
      // the previous tracked items aren't the same (also helps with not triggering while move wasn't complete),
      // tracker func exists
      if (
        visibleIndexes.length &&
        !isEqualArray(visibleIndexes, visibleIndexesRef.current) &&
        trackCarouselViewed
      ) {
        trackCarouselViewed(visibleIndexes);
        visibleIndexesRef.current = visibleIndexes; // Save tracked indexes
      }
    },
    [trackCarouselViewed]
  );

  const debouncedSwipeChange = useDebouncedCallback(handleSwipeChange, 500);

  useEffect(() => {
    if (!swiper) {
      return;
    }

    const currentSwiper = swiper;

    const handleIsInvisible = () => {
      currentSwiper.slides.forEach((slide) => {
        if (!slide.classList.contains("swiper-slide-visible")) {
          slide.classList.add("swiper-slide-invisible");
          slide.removeAttribute("aria-current");
        }
      });
      setHasHorizontalPadding(true);
    };
    const handleRemoveInvisible = () => {
      currentSwiper.slides.forEach((slide) => {
        slide.classList.remove("swiper-slide-invisible");
        slide.setAttribute("aria-current", "true");
      });
      setHasHorizontalPadding(false);
    };

    const handleTransitionEnd = () => {
      handleIsInvisible();
      handleRemoveInvisible();
    };

    if (slideShadowSpace) {
      currentSwiper.on("update", handleRemoveInvisible);
      currentSwiper.on("transitionEnd", handleTransitionEnd);
      handleIsInvisible();
    }

    swiper.on("activeIndexChange", debouncedSwipeChange);

    return () => {
      currentSwiper.off("transitionStart");
      currentSwiper.off("transitionEnd", handleTransitionEnd);
      currentSwiper.off("sliderMove");
      currentSwiper.off("update", handleRemoveInvisible);
      currentSwiper.off("activeIndexChange", debouncedSwipeChange);
    };
  }, [swiper, slideShadowSpace, debouncedSwipeChange]);

  return (
    <CarouselTemplate
      data-testid="carousel-template"
      role="listbox"
      aria-label="carousel"
    >
      <StyledSwiperWrapper
        className={classNames({
          ["slide-shadow-space"]: slideShadowSpace,
          ["with-vertical-padding"]: hasVerticalPadding,
          ["with-horizontal-padding"]: hasHorizontalPadding,
          ["is-product-carousel"]: isProductCarousel,
          ["is-retro-revival"]: isRetroRevival,
          ["fit-inside-container"]:
            isClient && visibleSlides >= carouselItems?.length,
          ["fit-regular"]: isClient && visibleSlides < carouselItems?.length,
          ["is-server-render"]: !isClient,
        })}
        marginSizeMobile={marginSizeMobile}
        slideShadowSpace={slideShadowSpace}
        data-testid="carousel-swiper-wrapper"
        ref={ref}
      >
        <Swiper
          initialSlide={0}
          spaceBetween={spaceBetween}
          slidesPerView={visibleSlides}
          slidesPerGroup={Math.floor(visibleSlides)}
          cssMode={!slideShadowSpace} // NOTE: Don't forget to use "smoothscroll-polyfill" package to fix animation on Safari. Check Swiper API
          mousewheel={true}
          onSwiper={onSwiper}
          speed={500}
          navigation={{
            prevEl: prevButton?.current,
            nextEl: nextButton?.current,
          }}
          onSlideChange={updateEdges}
          onFromEdge={updateEdges}
          onToEdge={updateEdges}
          onResize={updateEdges}
          onUpdate={updateEdges}
          watchSlidesVisibility
          slideVisibleClass="swiper-slide-visible"
        >
          {carouselItems && carouselItems.length === 0
            ? carouselItems.map((_, index) => (
                <SwiperSlide key={`carousel-card-fallback-${index}`}>
                  {isProductCarousel ? (
                    <ProductCardFallback />
                  ) : (
                    <BrandCardFallback />
                  )}
                </SwiperSlide>
              ))
            : null}

          {carouselItems?.map((item, index) => (
            <SwiperSlide
              key={index}
              role="option"
              aria-label={`product ${index + 1} of ${carouselItems.length}`}
              data-item-index={index}
            >
              {item}
            </SwiperSlide>
          ))}
        </Swiper>
      </StyledSwiperWrapper>
      <StyledButtonBack
        ref={prevButton}
        tabIndex={0}
        offsetPosition={arrowTopPosition || "50%"}
        data-testid="carousel-prev-button"
        disabled={edges.isBeginning}
      >
        <span className="sr-only">Previous products</span>
        <ArrowBack color="#999" role="presentation" />
      </StyledButtonBack>

      <StyledButtonNext
        tabIndex={0}
        ref={nextButton}
        offsetPosition={arrowTopPosition || "50%"}
        data-testid="carousel-next-button"
        disabled={edges.isEnd}
      >
        <span className="sr-only">Next products</span>
        <ArrowNext color="#999" role="presentation" />
      </StyledButtonNext>
    </CarouselTemplate>
  );
}

export default Carousel;
